import { defineStore } from 'pinia'
import { url$, module$ } from '../enums'
import { useApp } from './index'
import helper from '../utils/helper'
import { i18n } from '@/locales';

const _hf0b2370fbaeb472baddd7b4d9253eea2 = new helper();

/**
 * security store 
 **/
export const useSecurity = defineStore('app.security', {
    persist: {
        //F5後值依然存在
        serializer: {
            serialize: (state) => _hf0b2370fbaeb472baddd7b4d9253eea2.encryptStore(state, 'f0b2370fbaeb472baddd7b4d9253eea2'),
            deserialize: (value) => _hf0b2370fbaeb472baddd7b4d9253eea2.decryptStore(value, 'f0b2370fbaeb472baddd7b4d9253eea2'),
        },
        pick: ['userInfo'],
    },
    state: () => {
        return {
            userInfo: {
                account: '',
                name: '',
                roles: [] as string[],
                jti: ''
            },
        }
    },
    getters: {
        account: (state) => {
            return state.userInfo.account;
        }
    },
    actions: {
        /**
         * 身分是否已驗證(放在actions是因為如果手動刪除cookie因未異動state所以未觸發重新計算getter所以未符合實值)
         */
        isAuthenticated(): boolean {
            const result = (_hf0b2370fbaeb472baddd7b4d9253eea2.getCookie('token') != undefined);
            if (!result && !!this.userInfo.account) {
                this.reset();
            }
            return result;
        },
        /**
         * Json Web Token(放在actions是因為如果手動刪除cookie因未異動state所以未觸發重新計算getter所以未符合實值)
         * @returns JWT
         */
        token(): string | undefined {
            return _hf0b2370fbaeb472baddd7b4d9253eea2.getCookie('token');
        },
        /**
         * 執行登入
         * @param context store context
         * @param payload store payload
         */
        async login(payload: any): Promise<boolean> {
            const util$ = useApp().util$;
            try {
                //account
                this.$patch((state) => state.userInfo.account = payload.account);
                interface UserInfo {
                    success: boolean;
                    account: string;
                    username: string;
                    roles: string [];
                    token: string;
                    jti: string;
                    expired: string;
                }
                //執行驗證
                const result = await util$.http$.post<UserInfo>(url$.auth.concat('login'), payload);
                if (result.success) {
                    //account寫入state
                    result.account = payload.account;
                    util$.helper$.setCookie('token', result.token);
                    const expiredDate = util$.helper$.parseIsoDate(result.expired);
                    util$.log$.info('expired:', util$.helper$.formatDate(expiredDate));
                    this.$patch((state) => {
                        state.userInfo.name = result.username;
                        state.userInfo.roles = result.roles;
                        state.userInfo.jti = result.jti;
                    })
                    //轉往登入前嘗試訪問的頁（query.redirect）；無則回登入後首頁。
                    //redirect 為站內路徑時一律轉址（程式導向、外部連結帶入的站內落地頁皆放行）；
                    //僅擋外部網址（http(s):// 或 //），避免登入後被導向站外（釣魚式 open redirect）。
                    const redirect = util$.vueRouter$.currentRoute.value.query.redirect as string | undefined;
                    if (redirect && /^\/(?!\/)/.test(redirect)) {
                        util$.router$.replace(redirect);   // 站內 → 一律轉
                    } else {
                        util$.router$.replaceHome();         // 無 redirect 或外部網址 → 回 home
                    }
                    return true;
                }
                else {
                    throw new Error('Unknown error!');
                }
            }
            catch (error: any) {
                // 失敗取值統一經 extractError（status + code 一次解構，呼叫端不直接碰 .response/.data）
                const { status, code } = util$.http$.extractError(error);
                if (status == 401) {
                    if (code == 'ACCOUNT_LOCKED') {
                        //嘗試輸入太多太被鎖定
                        const lockoutPrompt = i18n.global.t('login.lockoutPrompt', {
                            maxLoginAttempts: util$.pinia$.app.parameters.login.maxLoginAttempts,
                            lockoutDuration: util$.pinia$.app.parameters.login.lockoutDuration
                        })
                        const lockoutDuration = util$.pinia$.app.parameters.login.lockoutDuration;
                        util$.pinia$.app.busyFor(lockoutPrompt, () => {
                            return new Promise(resolve => setTimeout(resolve, lockoutDuration * 60 * 1000));
                        });
                    } else {
                        const e = new Error(i18n.global.t('login.loginError'));
                        util$.handleError(e, { showMessage: true, context: 'secret.login' })
                    }
                } else if (status == 400 && code == 'INVALID_CAPTCHA') {
                    const e = new Error(i18n.global.t('login.badCaptcha'));
                    util$.handleError(e, { showMessage: true, context: 'secret.login' })
                }
            }
            return false;
        },
        /**
         * 執行登出
         * @param payload
         */
        async logout() {
            const util$ = useApp().util$;

            try {
                //未登入情況下不執行後台紀錄logout(因為必定401)
                if (this.isAuthenticated()) {
                    //執行登出資料處理
                    await util$.http$.post(url$.auth.concat("logout"), {
                    })
                }
            }
            finally {
                this.reset();
            }
        },
        /** 被動失去 session（JWT 過期 / 閒置逾時）：清登入狀態 + 顯示訊息 + 導 login 帶 redirect 回原頁。
            訊息用 messenger（存 store，轉址後 login 頁仍顯示）而非 toast（轉址會被洗掉）。
            與主動 logout 區別：logout 不帶 redirect、不記原頁；被動失效記原頁供登入後返回。 */
        sessionExpired(message?: string) {
            const util$ = useApp().util$;
            this.reset();   // 清狀態 +（內部）replace 至 login；惟 vue-router 導航為異步，下方同步讀取 currentRoute 時尚未更新，cur 仍為原頁
            if (message) {
                util$.messenger$.warn(message);   // messenger：轉址後 login 頁仍顯示
            }
            const cur = util$.vueRouter$.currentRoute.value;
            // cur 仍為「失效當下原頁」（reset 的導航尚未完成）；帶 redirect push 後發覆蓋 reset 的無 redirect 導向，登入後可回原頁。
            if (cur.path !== url$.login) {
                util$.router$.push({ path: url$.login, query: { redirect: cur.fullPath } });
            }
        },
        /**
         * 執行登出
         * @param payload
         */
        setToken(token: string) {
            const util$ = useApp().util$;
            util$.helper$.setCookie('token', token);
        },
        /**
         * 重置為初始狀態
         * @param context store context
         */
        reset() {
            const util$ = useApp().util$;
            util$.helper$.deleteCookie('token');
            this.$patch((state) => {
                state.userInfo.account = '';
                state.userInfo.name = '';
                state.userInfo.roles = [];
                state.userInfo.jti = '';
            });
            util$.router$.replace({ name: module$.login });
        }
    }
})